import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ImageIcon, X } from 'lucide-react';
import type { HqEmphasisAttachment } from '@kop/contracts';
import type { HqEmphasisRepository } from './repository';
import { useHqMedia } from '../../runtime/use-hq-media';

export function EmphasisPhoto({
  repository,
  eventId,
  attachment,
}: {
  repository: HqEmphasisRepository;
  eventId: string;
  attachment: HqEmphasisAttachment;
}) {
  const { host, asset, failed, renew } = useHqMedia(repository, eventId, attachment.manifestId);
  const url = asset?.url;
  return url && !failed ? (
    <img
      src={url}
      alt={attachment.fileName}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={renew}
    />
  ) : (
    <span ref={host} className="hqe-photo-placeholder">
      <ImageIcon size={24} />
      {failed ? '사진을 눌러 다시 열어 주세요' : '사진 불러오는 중'}
    </span>
  );
}

export function EmphasisFileViewer({
  repository,
  eventId,
  attachment,
  onClose,
}: {
  repository: HqEmphasisRepository;
  eventId: string;
  attachment: HqEmphasisAttachment;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const {
    asset,
    failed: error,
    renew,
    retry,
  } = useHqMedia(repository, eventId, attachment.manifestId, true);
  const url = asset?.url;
  useEffect(() => {
    dialog.current?.showModal();
    const previous = document.activeElement as HTMLElement | null;
    return () => {
      previous?.focus();
    };
  }, [repository, eventId, attachment.manifestId]);
  return createPortal(
    <dialog
      ref={dialog}
      className="hqe-dialog hqe-file-viewer"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header>
        <h2>{attachment.fileName}</h2>
        <button type="button" aria-label="자료 닫기" onClick={onClose}>
          <X size={20} />
        </button>
      </header>
      <div className="hqe-file-body">
        {error ? (
          <p role="alert">
            자료를 열지 못했습니다.{' '}
            <button type="button" onClick={retry}>
              다시 시도
            </button>
          </p>
        ) : !url ? (
          <p role="status">자료를 불러오는 중입니다.</p>
        ) : attachment.kind === 'photo' ? (
          <img src={url} alt={attachment.fileName} referrerPolicy="no-referrer" onError={renew} />
        ) : (
          <iframe title={attachment.fileName} src={url} referrerPolicy="no-referrer" />
        )}
      </div>
      <footer>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer">
            원본 열기·다운로드
          </a>
        )}
        <button type="button" onClick={onClose}>
          닫기
        </button>
      </footer>
    </dialog>,
    document.body,
  );
}
